import React from 'react';
import { Tooltip } from 'antd';
import moment from 'moment';
import {
	ComparisonMode,
	formatComparisonRangeLabel,
	getComparisonSeriesLabel,
} from '../../utils/AnalyticsComparisonUtils';

interface Props {
	currentStart: moment.Moment | null;
	currentEnd: moment.Moment | null;
	previousStart: moment.Moment | null;
	previousEnd: moment.Moment | null;
	currentColor: string;
	previousColor: string;
	mode: ComparisonMode;
	/**
	 * Swatch style for the legend keys. 'line' (default) renders a solid line
	 * for the current series and a dashed line for the previous series — used
	 * by line charts so the legend mirrors the chart's line styles. 'square'
	 * renders filled color squares, used by bar charts where the bars don't
	 * have a line/dash representation.
	 */
	swatchType?: 'line' | 'square';
}

const LEGEND_LINE_WIDTH = 24;
const LEGEND_LINE_HEIGHT = 2;
const COMPARISON_LINE_DASH = '4 4';

const SolidLineSwatch = ({ color }: { color: string }) => (
	<span
		className="inline-block h-0.5 w-6 shrink-0"
		style={{ backgroundColor: color }}
		aria-hidden="true"
	/>
);

const DashedLineSwatch = ({ color }: { color: string }) => (
	<svg
		width={LEGEND_LINE_WIDTH}
		height={LEGEND_LINE_HEIGHT}
		className="inline-block shrink-0"
		aria-hidden="true"
	>
		<line
			x1="0"
			y1="1"
			x2={LEGEND_LINE_WIDTH}
			y2="1"
			stroke={color}
			strokeWidth={LEGEND_LINE_HEIGHT}
			strokeDasharray={COMPARISON_LINE_DASH}
		/>
	</svg>
);

const SquareSwatch = ({ color }: { color: string }) => (
	<span
		className="inline-block h-2.5 w-2.5 shrink-0 rounded-[2px]"
		style={{ backgroundColor: color }}
		aria-hidden="true"
	/>
);

const ComparisonLegend = ({
	currentStart,
	currentEnd,
	previousStart,
	previousEnd,
	currentColor,
	previousColor,
	mode,
	swatchType = 'line',
}: Props) => {
	const previousLabel = getComparisonSeriesLabel(mode);
	const hasPrevious = !!(previousStart && previousEnd);
	const currentRange = formatComparisonRangeLabel(currentStart, currentEnd);
	const previousRange = hasPrevious
		? formatComparisonRangeLabel(previousStart, previousEnd)
		: '(no data)';

	const useSquares = swatchType === 'square';
	const currentSwatch = useSquares ? (
		<SquareSwatch color={currentColor} />
	) : (
		<SolidLineSwatch color={currentColor} />
	);
	const previousSwatch = useSquares ? (
		<SquareSwatch color={previousColor} />
	) : (
		<DashedLineSwatch color={previousColor} />
	);

	return (
		<div className="mt-2 flex min-w-0 items-center justify-center gap-x-6 overflow-hidden">
			<span className="inline-flex min-w-0 items-center gap-2">
				{currentSwatch}
				<span className="footnoteregular shrink-0 text-character-primary">Current Period</span>
				<Tooltip title={currentRange}>
					<span className="footnoteregular truncate text-character-secondary">{currentRange}</span>
				</Tooltip>
			</span>
			<span className="inline-flex min-w-0 items-center gap-2">
				{previousSwatch}
				<span className="footnoteregular shrink-0 text-character-primary">{previousLabel}</span>
				<Tooltip title={previousRange}>
					<span className="footnoteregular truncate text-character-secondary">{previousRange}</span>
				</Tooltip>
			</span>
		</div>
	);
};

export default ComparisonLegend;
